/**
 * CSV export for the platform-signups report. Ported from
 * aieweb/components/admin/signups-content.tsx (csvEscape / buildCsv /
 * csvFilename) — the escaping, formula-injection defense, and forensic footer
 * are preserved exactly. The one behavioral change vs the old page: the rows
 * passed in come from the server `export=true` slice (the FULL filtered set),
 * not a client-capped view.
 */
import type {
  PlatformSignupOrgRow,
  PlatformOrphanAccountRow,
  SignupSegment,
} from "@/services/adminApi";

// ---------------------------------------------------------------------------
// CSV escaping (RFC 4180 + formula-injection defense)
// ---------------------------------------------------------------------------

/** Cells starting with these characters can be interpreted as formulas by
 * Excel / Google Sheets / LibreOffice. Prefixing with a single quote defeats
 * formula execution while keeping the cell readable. */
const FORMULA_INJECTION_PREFIXES = ["=", "+", "-", "@", "\t", "\r", "\n"];

/** Escape a single CSV cell per RFC 4180 with formula-injection defense. */
export function csvEscape(value: unknown): string {
  if (value === null || value === undefined) return '""';
  let s = String(value);
  if (s.length > 0 && FORMULA_INJECTION_PREFIXES.includes(s[0])) {
    s = "'" + s;
  }
  return '"' + s.replace(/"/g, '""') + '"';
}

// ---------------------------------------------------------------------------
// CSV builders
// ---------------------------------------------------------------------------

const ORG_HEADERS = [
  "orgId",
  "orgName",
  "ownerName",
  "ownerEmail",
  "signupDate",
  "lastActivityDate",
  "daysSinceLastActivity",
  "tokensUsed",
  "coreChatCount",
  "lastChatAt",
  "studioProjectCount",
  "studioLastProjectAt",
  "isTestName",
  "isInternalEmail",
  "isDemoOrg",
];

const ORPHAN_HEADERS = ["accountId", "name", "email", "signupDate"];

function orgRowToCsv(row: PlatformSignupOrgRow): string {
  return [
    row.orgId,
    row.orgName,
    row.ownerName ?? "",
    row.ownerEmail ?? "",
    row.signupDate,
    row.lastActivityDate ?? "",
    row.daysSinceLastActivity ?? "",
    row.tokensUsed ?? "",
    row.coreChatCount,
    row.lastChatAt ?? "",
    row.studioProjectCount ?? "",
    row.studioLastProjectAt ?? "",
    row.isTestName,
    row.isInternalEmail,
    row.isDemoOrg,
  ]
    .map(csvEscape)
    .join(",");
}

function orphanRowToCsv(row: PlatformOrphanAccountRow): string {
  return [row.accountId, row.name ?? "", row.email, row.signupDate]
    .map(csvEscape)
    .join(",");
}

/** Optional filter/sort context for the CSV footer. Unset fields read "(none)". */
export interface CsvExportContext {
  /** Active date-range preset label (e.g., "Last 7 days", "All time"). */
  dateRangeLabel?: string;
  /** Active text filter (the search box value). */
  textFilter?: string;
  /** Active sort column name (display label). */
  sortColumn?: string;
  /** Active sort direction. */
  sortDirection?: "asc" | "desc";
}

/** Build the full CSV string for a segment. Exported for tests. */
export function buildCsv(
  segment: SignupSegment,
  rows: (PlatformSignupOrgRow | PlatformOrphanAccountRow)[],
  viewerEmail: string,
  generatedAtIso: string,
  exportContext?: CsvExportContext
): string {
  const lines: string[] = [];
  if (segment === "orphan") {
    lines.push(ORPHAN_HEADERS.map(csvEscape).join(","));
    for (const r of rows as PlatformOrphanAccountRow[]) {
      lines.push(orphanRowToCsv(r));
    }
  } else {
    lines.push(ORG_HEADERS.map(csvEscape).join(","));
    for (const r of rows as PlatformSignupOrgRow[]) {
      lines.push(orgRowToCsv(r));
    }
  }
  lines.push(
    `# CONFIDENTIAL — generated for ${viewerEmail} at ${generatedAtIso} UTC — for internal use only`
  );
  const dr = exportContext?.dateRangeLabel ?? "(none)";
  const tf = exportContext?.textFilter ? `"${exportContext.textFilter}"` : "(none)";
  const sc = exportContext?.sortColumn ?? "(none)";
  const sd = exportContext?.sortDirection ?? "(none)";
  lines.push(`# Filter: ${dr}, Text filter: ${tf}, Sort: ${sc} ${sd}`);
  // CRLF line terminators per RFC 4180 so Excel on Windows parses cleanly.
  return lines.join("\r\n");
}

/** Build the CSV filename: parslee-signups-<segment>-<YYYY-MM-DD-HHMM>.csv. */
export function csvFilename(segment: SignupSegment, now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const stamp =
    `${now.getUTCFullYear()}-${pad(now.getUTCMonth() + 1)}-${pad(now.getUTCDate())}` +
    `-${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}`;
  return `parslee-signups-${segment}-${stamp}.csv`;
}

/** Trigger a browser download of a CSV string. Not exercised by unit tests
 *  (DOM-only); the escaping/format logic above is what the tests pin. */
export function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
